import type {TicketFiltersValue, TicketState, TicketType} from './types';

import {BottomSheet, Button, Checkbox, Input} from '@ds/components';
import {useEffect, useState} from 'react';

const TICKET_TYPE_OPTIONS: Array<{value: TicketType; label: string}> = [
  {value: 'general', label: 'عمومی'},
  {value: 'order', label: 'سفارش'},
  {value: 'loan', label: 'وام'}
];

const TICKET_STATE_OPTIONS: Array<{value: TicketState; label: string}> = [
  {value: 'created', label: 'ایجاد شده'},
  {value: 'done', label: 'انجام شده'},
  {value: 'inprogress', label: 'در حال بررسی'},
  {value: 'deleted', label: 'حذف شده'},
  {value: 'rejected', label: 'رد شده'}
];

const EMPTY_FILTERS: TicketFiltersValue = {
  order_code: '',
  types: [],
  states: []
};

type TicketFiltersProps = {
  show: boolean;
  filters: TicketFiltersValue;
  onApply: (filters: TicketFiltersValue) => void;
  onClose: () => void;
};

const toggleValue = <T extends string>(values: T[], value: T): T[] =>
  values.includes(value) ? values.filter((item) => item !== value) : [...values, value];

const TicketFilters: React.FC<TicketFiltersProps> = ({show, filters, onApply, onClose}) => {
  const [draftFilters, setDraftFilters] = useState<TicketFiltersValue>(filters);

  useEffect(() => {
    if (show) {
      setDraftFilters(filters);
    }
  }, [filters, show]);

  const handleClear = () => {
    setDraftFilters(EMPTY_FILTERS);
    onApply(EMPTY_FILTERS);
  };

  return (
    <BottomSheet
      show={show}
      onClose={onClose}
      defaultHeader
      title="فیلتر درخواست‌ها"
      footer={
        <div className="flex gap-2 border-t border-gray-03 p-4">
          <Button
            fullWidth
            variant="outline"
            color="secondary"
            onClick={handleClear}
            disabled={
              !draftFilters.order_code.trim() &&
              draftFilters.types.length === 0 &&
              draftFilters.states.length === 0
            }
          >
            حذف فیلترها
          </Button>
          <Button fullWidth onClick={() => onApply(draftFilters)}>
            اعمال فیلتر
          </Button>
        </div>
      }
    >
      <div className="flex flex-col gap-6 p-4">
        <Input
          label="کد سفارش"
          value={draftFilters.order_code}
          placeholder="مثلاً a76F23"
          allowClear
          onClear={() => setDraftFilters((current) => ({...current, order_code: ''}))}
          onChange={(event) =>
            setDraftFilters((current) => ({...current, order_code: event.target.value}))
          }
        />

        <fieldset className="flex flex-col gap-3">
          <legend className="mb-1 text-body-sm-emphasis text-neutral-fg">نوع درخواست</legend>
          {TICKET_TYPE_OPTIONS.map((option) => (
            <Checkbox
              key={option.value}
              checked={draftFilters.types.includes(option.value)}
              onChange={() =>
                setDraftFilters((current) => ({
                  ...current,
                  types: toggleValue(current.types, option.value)
                }))
              }
            >
              {option.label}
            </Checkbox>
          ))}
        </fieldset>

        <fieldset className="flex flex-col gap-3">
          <legend className="mb-1 text-body-sm-emphasis text-neutral-fg">وضعیت درخواست</legend>
          {TICKET_STATE_OPTIONS.map((option) => (
            <Checkbox
              key={option.value}
              checked={draftFilters.states.includes(option.value)}
              onChange={() =>
                setDraftFilters((current) => ({
                  ...current,
                  states: toggleValue(current.states, option.value)
                }))
              }
            >
              {option.label}
            </Checkbox>
          ))}
        </fieldset>
      </div>
    </BottomSheet>
  );
};

export default TicketFilters;
